import { Card, Grid } from "@mui/material";
import { styled } from "@mui/system";

import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import HorizontalBarChart from "components/Charts/BarCharts/HorizontalBarCharts";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

function BetStats({ data }) {
  const [totalBetsChartData, setTotalBetsChartData] = useState({
    totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
    labels: [],
    datasets: [
      {
        label: "Total Bets",
        color: "dark",
        data: [],
      },
    ],
  });
  const [totalBetOddsChartData, setTotalBetOddsChartData] = useState({
    totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
    labels: [],
    datasets: [
      {
        label: "Total Bet Odds",
        color: "dark",
        data: [],
      },
    ],
  });

  const handleHorizontalBarChart = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    if (data) {
      setTotalBetsChartData(data.totalBetsChartData);
      setTotalBetOddsChartData(data.totalBetOddsChartData);
    }
  }, [data]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <SoftTypography variant="h5" fontWeight="medium">
          {data.title}
        </SoftTypography>
      </Grid>
      <Grid item xs={3} md={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Card>
              <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
                <SoftBox lineHeight={1}>
                  <SoftTypography variant="button" fontWeight="medium" color="text">
                    Total Bets
                  </SoftTypography>
                  <SoftTypography variant="h5" fontWeight="bold">
                    {totalBetsChartData.totals[0].all}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={12}>
            <Card>
              <SoftBox lineHeight={1} p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12}>
                    <SoftTypography variant="button" fontWeight="medium" color="text">
                      Total Betted Odds
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <SoftTypography variant="label" fontWeight="bold">
                      {totalBetOddsChartData.totals[0].all}
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                      <Grid item xs={4} md={4} pl={"8px !important"}>
                        <SoftTypography variant="caption" fontWeight="medium" color="success">
                          {totalBetOddsChartData.totals[0].won}
                        </SoftTypography>
                        <SoftTypography
                          variant="caption"
                          fontWeight="regular"
                          color="secondary"
                          fontSize="9px"
                        >
                          won
                        </SoftTypography>
                      </Grid>
                      <Grid item xs={4} md={4} pl={"8px !important"}>
                        <SoftTypography variant="caption" fontWeight="medium" color="error">
                          {totalBetOddsChartData.totals[0].lost}
                        </SoftTypography>
                        <SoftTypography
                          variant="caption"
                          fontWeight="regular"
                          color="secondary"
                          fontSize="9px"
                        >
                          lost
                        </SoftTypography>
                      </Grid>
                      <Grid item xs={4} md={4} pl={"8px !important"}>
                        <SoftTypography variant="caption" fontWeight="medium" color="info">
                          {totalBetOddsChartData.totals[0].unsettled}
                        </SoftTypography>
                        <SoftTypography
                          variant="caption"
                          fontWeight="regular"
                          color="secondary"
                          fontSize="9px"
                        >
                          unsettled
                        </SoftTypography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={12}>
            <Card>
              <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
                <SoftBox lineHeight={1}>
                  <SoftTypography variant="button" fontWeight="medium" color="text">
                    Refund Rate
                  </SoftTypography>
                  <HtmlTooltip
                    placement="right"
                    title={
                      <React.Fragment>{totalBetOddsChartData.totals[0].refund} Odds</React.Fragment>
                    }
                  >
                    <SoftTypography variant="h5" fontWeight="bold">
                      {(
                        (totalBetOddsChartData.totals[0].refund * 100) /
                        totalBetOddsChartData.totals[0].settled
                      ).toFixed(2)}{" "}
                      %
                    </SoftTypography>
                  </HtmlTooltip>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={9} md={9}>
        <Grid container spacing={3}>
          <Grid item xs={6} md={6}>
            <HorizontalBarChart
              title="Total Bets per Platform"
              chart={totalBetsChartData}
              informParent={handleHorizontalBarChart}
            />
          </Grid>
          <Grid item xs={6} md={6}>
            <HorizontalBarChart
              title="Total Betted Odds per Platform"
              chart={totalBetOddsChartData}
              informParent={handleHorizontalBarChart}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={12}></Grid>
    </Grid>
  );
}

// Typechecking props for the DataTable
BetStats.propTypes = { data: PropTypes.object };

export default BetStats;
